import { useQueryClient } from '@tanstack/vue-query';

import { projectKeys, projectQueryOptions } from '@/entities/project';
import { type Team, teamKeys } from '@/entities/team';

import { useTeamCookie } from './use-team-cookie';

export function useTeamSwitch() {
  const queryClient = useQueryClient();
  const { setTeamCookie, clearTeamCookie } = useTeamCookie();

  async function switchToTeam(nextTeam: Team) {
    queryClient.removeQueries({ queryKey: projectKeys.project() });

    queryClient.setQueryData(teamKeys.team(nextTeam.uuid), nextTeam);

    setTeamCookie(nextTeam.uuid);

    if (!nextTeam.is_billing_ready) {
      clearCacheQueries();
      return;
    }

    const project = await queryClient.ensureQueryData(projectQueryOptions());

    queryClient.setQueryData(projectKeys.project(project.uuid), project);

    await queryClient.invalidateQueries({ queryKey: projectKeys.projects() });

    clearCacheQueries();
  }

  async function switchToPersonalAccount() {
    queryClient.removeQueries({ queryKey: projectKeys.project() });

    clearTeamCookie();

    const project = await queryClient.ensureQueryData(projectQueryOptions());

    queryClient.setQueryData(projectKeys.project(project.uuid), project);

    await queryClient.invalidateQueries({ queryKey: projectKeys.projects() });
    await queryClient.invalidateQueries({ queryKey: teamKeys.teams() });

    clearCacheQueries();
  }

  function clearCacheQueries() {
    const exceptQueryKey = ['project', 'profile', 'team', 'teams', 'notifications'];

    queryClient.removeQueries({
      predicate(query) {
        return !exceptQueryKey.some((key) => query.queryKey.includes(key));
      },
    });
  }

  return { switchToTeam, switchToPersonalAccount };
}
